<!-- Read-only карточка ЭТрН с единым новым представлением титулов. -->
<template>
  <div class="etrn-info__content">
    <FlowDetails
      v-if="documentFlowVisible"
      :flow="documentFlow"
      :title-details="mainTitleDetails"
      :additional-title-details="additionalTitleDetails"
      :read-only="readOnly"
      :inspectable="readOnly"
      :variant="variant"
      @title-action="handleTitleAction"
    />

  </div>
</template>

<script>
import FlowDetails from "@/components/Edo/ConsignmentNote/FlowDetails.vue";
import { formatDate, formatDateAndTime } from "@/utils/dateUtils";
import { consignmentNoteTitleDefinitions } from "@/utils/edoConsignmentNoteTitleUtils";
import { formatBigNumber } from "@/utils/numberUtils";

export default {
  name: "EdoConsignmentNoteInfoTab",

  components: {
    FlowDetails,
  },

  props: {
    document: {
      type: Object,
      required: true,
    },
    sabyUrl: {
      type: String,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "card",
      validator: (value) => ["card", "embedded", "flat"].includes(value),
    },
  },

  emits: ["create-title1", "edit-title2", "edit-title3", "edit-title4", "edit-redirect", "edit-replacement"],

  computed: {
    documentFlow() {
      return this.document.document_flow || {};
    },

    documentData() {
      return this.document.parsed_data || {};
    },

    documentFlowVisible() {
      const mainSteps = Array.isArray(this.documentFlow.steps) && this.documentFlow.steps.length > 0;
      const additionalGroups = Array.isArray(this.documentFlow.additional_groups) &&
        this.documentFlow.additional_groups.length > 0;

      return mainSteps || additionalGroups;
    },

    titleDetails() {
      return consignmentNoteTitleDefinitions(this.document).map((title) => ({
        key: title.key,
        group: title.group,
        number: title.number,
        label: title.numberLabel,
        role: title.role,
        title: title.title,
        description: title.description,
        hasData: this.titleHasData(title),
        sections: this.titleSections(title).map((section) => this.panelSection(section)),
      }));
    },

    // Здесь остаются только реальные данные титулов. Статус и действия новый
    // flow связывает с backend document_flow отдельно.
    mainTitleDetails() {
      return this.titleDetails.filter((title) => title.group === "main");
    },

    additionalTitleDetails() {
      return this.titleDetails.filter((title) => title.group === "additional");
    },
  },

  methods: {
    // Наличие фактических данных не определяет статус ЭДО: статус по-прежнему
    // приходит из document_flow, здесь выбирается только вид панели данных.
    titleHasData(title) {
      if (title.titleRecord || this.documentData[title.key]) return true;

      return title.key === "title1" && Object.keys(this.documentData).length > 0;
    },

    panelSection(section) {
      if (section.key !== "route") return section;

      const routeKeys = ["loading_point", "delivery_point"];

      return {
        ...section,
        routePoints: routeKeys
          .map((key) => section.rows.find((row) => row.key === key))
          .filter(Boolean),
        rows: section.rows.filter((row) => !routeKeys.includes(row.key)),
      };
    },

    titleSections(title) {
      if (title.number === 1) return this.title1Sections();
      if (title.number === 2) return this.title2Sections();
      if (title.number === 3) return this.title3Sections();
      if (title.number === 4) return this.title4Sections();
      if (title.number === 7) return this.redirectSections();
      if (title.number === 44) return this.replacementSections();

      return [
        {
          key: "planned",
          title: "Данные титула",
          rows: [
            this.previewRow(
              `${title.key}_planned`,
              title.numberLabel,
              title.titleRecord
                ? "Данные титула получены из Saby. Детальная форма титула в Трако пока не реализована."
                : "Титул ещё не заполнен. Детальная форма появится после реализации этого этапа ЭТрН."
            ),
          ],
        },
      ];
    },

    title1Sections() {
      return [
        {
          key: "document",
          title: "Документ",
          rows: [
            this.previewRow(
              "document_number",
              "Номер",
              this.documentData.document_number || this.document.document_number
            ),
            this.previewRow(
              "document_date",
              "Дата",
              this.formatDateValue(this.documentData.document_date || this.document.document_date)
            ),
          ],
        },
        {
          key: "participants",
          title: "Участники",
          rows: [
            this.participantRow("shipper", "Грузоотправитель"),
            this.participantRow("consignee", "Грузополучатель"),
            this.participantRow("carrier", "Перевозчик"),
            this.participantRow("loading_person", "Лицо, выполняющее погрузку", this.documentData.loading_point?.loading_person),
            this.participantRow("infrastructure_owner", "Владелец инфраструктуры", this.documentData.loading_point?.infrastructure_owner),
          ],
        },
        {
          key: "route",
          title: "Маршрут",
          rows: [
            this.pointRow("loading_point", "Погрузка"),
            this.pointRow("delivery_point", "Выгрузка"),
            this.previewRow(
              "loading_basis_document",
              "Основание погрузки",
              this.documentValue(this.documentData.loading_point?.loading_basis_document)
            ),
            this.previewRow(
              "loading_worker",
              "Работник при погрузке",
              this.personValue(this.documentData.loading_point?.loading_worker)
            ),
          ],
        },
        {
          key: "cargo",
          title: "Груз",
          rows: this.cargoRows(),
        },
        {
          key: "transport",
          title: "Транспорт",
          rows: [
            this.vehicleRow("vehicle", "ТС", this.documentData.vehicle),
            this.vehicleRow("trailer", "Прицеп", this.firstTrailer()),
          ],
        },
        {
          key: "driver",
          title: "Водитель",
          rows: [
            this.driverRow(),
          ],
        },
      ].map((section) => ({
        ...section,
        rows: section.rows.filter(Boolean),
      }));
    },

    title2Sections() {
      const data = this.titleData("title2");

      return [
        {
          key: "title2_carrier_acceptance",
          title: "Приём груза перевозчиком",
          rows: [
            this.titleFieldRow("title2_acceptance_operation", "Содержание операции", data.acceptance_operation || data.carrier_acceptance_operation),
            this.titleFieldRow("title2_cargo_condition_note", "Замечания по состоянию груза", data.cargo_condition_note),
            this.titleFieldRow("title2_cargo_weight_note", "Замечания по массе груза", data.cargo_weight_note),
            this.titleFieldRow("title2_cargo_places_note", "Замечания по количеству мест", data.cargo_places_note),
            this.titleFieldRow("title2_cargo_package_note", "Замечания по упаковке", data.cargo_package_note),
            this.titleFieldRow("title2_cargo_marking_note", "Замечания по маркировке", data.cargo_marking_note),
            this.titleFieldRow("title2_cargo_seal_note", "Замечания по пломбам", data.cargo_seal_note),
            this.titleFieldRow("title2_arrival_note_at", "Фактическое прибытие", this.formatDateTimeValue(data.arrival_note_at)),
            this.titleFieldRow("title2_departure_note_at", "Фактическое убытие", this.formatDateTimeValue(data.departure_note_at)),
            this.titleFieldRow("title2_acceptance_note_at", "Окончание приёмки груза", this.formatDateTimeValue(data.acceptance_note_at)),
          ],
        },
        {
          key: "title2_signer",
          title: "Подписант",
          rows: [
            this.titleFieldRow("title2_carrier_signer", "Перевозчик", this.personValue(data.carrier_signer)),
          ],
        },
      ];
    },

    title3Sections() {
      const data = this.titleData("title3");

      return [
        {
          key: "title3_acceptance",
          title: "Приёмка грузополучателем",
          rows: [
            this.titleFieldRow("title3_acceptance_result", "Результат приёмки", this.acceptanceResultLabel(data.acceptance_result)),
            this.titleFieldRow("title3_acceptance_operation", "Содержание операции", data.acceptance_operation),
            this.titleFieldRow("title3_rejection_reason", "Причина отказа", data.rejection_reason),
            this.titleFieldRow("title3_common_cargo_condition", "Общее состояние груза", data.common_cargo_condition),
            this.titleFieldRow("title3_delivery_point", "Адрес выгрузки", this.addressValue(data.delivery_point)),
          ],
        },
        {
          key: "title3_timing",
          title: "Время доставки",
          rows: [
            this.titleFieldRow("title3_accepted_at", "Фактическое прибытие", this.formatDateTimeValue(data.accepted_at)),
            this.titleFieldRow("title3_departed_at", "Фактическое убытие", this.formatDateTimeValue(data.departed_at)),
            this.titleFieldRow("title3_planned_arrival_at", "Заявленное прибытие", this.formatDateTimeValue(data.planned_arrival_at)),
          ],
        },
        {
          key: "title3_signer",
          title: "Подписант",
          rows: [
            this.titleFieldRow("title3_consignee_signer", "Грузополучатель", this.personValue(data.consignee_signer)),
          ],
        },
      ];
    },

    title4Sections() {
      const data = this.titleData("title4");

      return [
        {
          key: "title4_carrier_issue",
          title: "Выдача груза перевозчиком",
          rows: [
            this.titleFieldRow("title4_issue_operation", "Содержание операции", data.issue_operation || data.carrier_issue_operation),
            this.titleFieldRow("title4_cargo_condition_note", "Замечания по состоянию груза", data.cargo_condition_note),
            this.titleFieldRow("title4_unloading_work_note", "Замечания по выгрузке", data.unloading_work_note),
          ],
        },
        {
          key: "title4_timing",
          title: "Время выдачи",
          rows: [
            this.titleFieldRow("title4_arrival_note_at", "Фактическое прибытие", this.formatDateTimeValue(data.arrival_note_at)),
            this.titleFieldRow("title4_departure_note_at", "Фактическое убытие", this.formatDateTimeValue(data.departure_note_at)),
            this.titleFieldRow("title4_unloading_note_at", "Окончание выгрузки", this.formatDateTimeValue(data.unloading_note_at)),
          ],
        },
        {
          key: "title4_signer",
          title: "Подписант",
          rows: [
            this.titleFieldRow("title4_carrier_signer", "Перевозчик", this.personValue(data.carrier_signer)),
          ],
        },
      ];
    },

    redirectSections() {
      const data = this.titleData("redirect");

      return [
        {
          key: "redirect_operation",
          title: "Переадресовка",
          rows: [
            this.titleFieldRow("redirect_reason", "Причина", data.redirect_reason),
            this.titleFieldRow("new_unloading_at", "Новая дата и время выгрузки", this.formatDateTimeValue(data.new_unloading_at)),
            this.titleFieldRow("new_delivery_point", "Новый адрес", this.addressValue(data.new_delivery_point)),
          ],
        },
        {
          key: "redirect_contact",
          title: "Контакт",
          rows: [
            this.titleFieldRow("redirect_contact", "Контакт по переадресовке", this.contactValue(data.redirect_contact)),
          ],
        },
        {
          key: "redirect_signer",
          title: "Подписант",
          rows: [
            this.titleFieldRow("redirect_carrier_signer", "Перевозчик", this.personValue(data.carrier_signer)),
          ],
        },
      ];
    },

    replacementSections() {
      const data = this.titleData("replacement");

      return [
        {
          key: "replacement_operation",
          title: "Замена",
          rows: [
            this.titleFieldRow("replacement_operation", "Операция", data.replacement_operation),
            this.titleFieldRow("replacement_reason", "Причина", data.replacement_reason),
            this.titleFieldRow("replacement_at", "Дата и время", this.formatDateTimeValue(data.replacement_at)),
          ],
        },
        {
          key: "replacement_transport",
          title: "Новые водитель и ТС",
          rows: [
            this.titleFieldRow("replacement_drivers", "Водители", this.driversValue(data.replacement_drivers)),
            this.vehicleRow("replacement_vehicle", "ТС", data.replacement_vehicle),
          ],
        },
        {
          key: "replacement_signer",
          title: "Подписант",
          rows: [
            this.titleFieldRow("replacement_carrier_signer", "Перевозчик", this.personValue(data.carrier_signer)),
          ],
        },
      ];
    },

    participantRow(key, label, participant = null) {
      const item = participant || this.documentData[key];
      if (!item) return this.previewRow(key, label, null);

      const details = [
        item.name,
        item.inn ? `ИНН ${item.inn}` : null,
        item.kpp ? `КПП ${item.kpp}` : null,
        this.addressValue(item.address || item),
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    pointRow(key, label) {
      const point = this.documentData[key];
      if (!point) return this.previewRow(key, label, null);

      const details = [
        this.addressValue(point),
        point.comment,
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    cargoRows() {
      const items = Array.isArray(this.documentData.cargo_items)
        ? this.documentData.cargo_items
        : [];
      if (!items.length) return [this.previewRow("cargo_empty", "Груз", null)];

      return items.flatMap((item, index) => {
        const parts = [
          item.title,
          item.quantity_places ? `${item.quantity_places} мест` : null,
          item.weight_gross_kg ? `${item.weight_gross_kg} кг` : null,
          item.volume_m3 ? `${item.volume_m3} м³` : null,
          item.value_amount ? `${this.formatBigNumber(item.value_amount)} ₽` : null,
        ].filter(Boolean);

        return [
          this.previewRow(`cargo_${index}`, `Груз ${index + 1}`, parts.join(", ")),
          this.previewRow(
            `cargo_${index}_description`,
            `Дополнительные сведения груза ${index + 1}`,
            item.description
          ),
        ].filter(Boolean);
      });
    },

    firstTrailer() {
      return Array.isArray(this.documentData.trailers)
        ? this.documentData.trailers[0]
        : null;
    },

    vehicleRow(key, label, vehicle) {
      if (!vehicle) return this.previewRow(key, label, null);

      const details = [
        vehicle.registration_number,
        vehicle.model,
        vehicle.consignment_note_vehicle_type || vehicle.etrn_vehicle_type,
        vehicle.ownership_type,
      ].filter(Boolean);

      return this.previewRow(key, label, details.join(", "));
    },

    driverRow() {
      const drivers = this.documentData.drivers;
      const driver = Array.isArray(drivers) ? drivers[0] : this.documentData.driver;
      if (!driver) return this.previewRow("driver", "Водитель", null);

      const details = [
        this.personValue(driver),
        driver.phone,
        driver.license_number
          ? `ВУ ${driver.license_series || ""} ${driver.license_number}`.trim()
          : null,
      ].filter(Boolean);

      return this.previewRow("driver", "Водитель", details.join(", "));
    },

    driversValue(drivers) {
      const items = Array.isArray(drivers) ? drivers : [];
      if (!items.length) return "";

      return items.map((driver) => this.personValue(driver)).filter(Boolean).join(", ");
    },

    contactValue(contact) {
      if (!contact) return "";

      return [
        this.personValue(contact),
        contact.phone,
        contact.email,
      ].filter(Boolean).join(", ");
    },

    documentValue(document) {
      if (!document) return "";

      return [
        document.name,
        document.number ? `№${document.number}` : null,
        document.date ? this.formatDateValue(document.date) : null,
      ].filter(Boolean).join(", ");
    },

    personValue(person) {
      if (!person) return "";

      return [
        person.last_name,
        person.first_name,
        person.middle_name || person.patronymic,
        person.position,
      ].filter(Boolean).join(" ");
    },

    addressValue(value) {
      if (!value) return "";
      if (typeof value === "string") return value;

      return (
        value.address ||
        value.address_text ||
        value.full_address ||
        [value.index, value.region_code, value.city, value.street, value.house]
          .filter(Boolean)
          .join(", ")
      );
    },

    previewRow(key, label, value) {
      return {
        key,
        label,
        value: this.formatPreviewValue(value),
      };
    },

    titleFieldRow(key, label, value) {
      return this.previewRow(key, label, value);
    },

    titleData(key) {
      return this.documentData[key] || {};
    },

    acceptanceResultLabel(value) {
      if (value === "accepted") return "Груз принят";
      if (value === "rejected") return "Отказ в приёмке";

      return value;
    },

    formatPreviewValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      return value;
    },

    formatBigNumber(value) {
      return formatBigNumber(value);
    },

    formatDateValue(value) {
      return value ? formatDate(value) : "—";
    },

    formatDateTimeValue(value) {
      return value ? formatDateAndTime(value) : "—";
    },

    handleTitleAction({ action }) {
      if (action.type === "saby" && this.sabyUrl) {
        window.open(this.sabyUrl, "_blank");
      } else if (action.type === "create-title1") {
        this.$emit("create-title1");
      } else if (action.type === "edit-title2") {
        this.$emit("edit-title2");
      } else if (action.type === "edit-title3") {
        this.$emit("edit-title3");
      } else if (action.type === "edit-title4") {
        this.$emit("edit-title4");
      } else if (action.type === "edit-redirect") {
        this.$emit("edit-redirect");
      } else if (action.type === "edit-replacement") {
        this.$emit("edit-replacement");
      }
    },
  },
};
</script>

<style scoped>
.etrn-info__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

</style>
